import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import API from "../api/axios";
import { saveAuth, isClient } from "../auth/auth";
import { NAVY, NAVY_SOFT, ACCENT } from "../theme";
import logo from "../assets/logo.jpg";

const FEATURES = [
  {
    icon: <WorkOutlineIcon sx={{ fontSize: 18 }} />,
    text: "Track every open position in one place",
  },
  {
    icon: <GroupsOutlinedIcon sx={{ fontSize: 18 }} />,
    text: "See candidates and who submitted them",
  },
  {
    icon: <InsightsOutlinedIcon sx={{ fontSize: 18 }} />,
    text: "Pipeline freshness and recruiter insights",
  },
];

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [fieldErr, setFieldErr] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const fe = { email: "", password: "" };
    if (!email) fe.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      fe.email = "Enter a valid email";
    if (!password) fe.password = "Password is required";
    setFieldErr(fe);
    return !fe.email && !fe.password;
  };

  const handleSubmit = async () => {
    setError("");

    if (!validate()) return;

    setLoading(true);

    try {
      const { data } = await API.post("/auth/login", {
        email,
        password,
      });

      saveAuth(data);
      // Clients go to their own read-only portal; everyone else to the main app.
      navigate(isClient() ? "/client" : "/");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // subtle dot-grid pattern as a data URI (no asset file needed)
  const dotPattern =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Ccircle cx='2' cy='2' r='1' fill='%23ffffff' fill-opacity='0.08'/%3E%3C/svg%3E\")";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* ---------- Brand panel ---------- */}
      <Box
        sx={{
          flex: { md: 1.05 },
          position: "relative",
          color: "#fff",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: 6,
          overflow: "hidden",
          background: `linear-gradient(150deg, ${NAVY} 0%, ${NAVY_SOFT} 55%, #1d3a6b 100%)`,
        }}
      >
        {/* pattern overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: dotPattern,
            backgroundSize: "32px 32px",
            opacity: 0.9,
          }}
        />
        {/* glow accents */}
        <Box
          sx={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 340,
            height: 340,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(71,85,105,0.55), transparent 70%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -140,
            left: -100,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(29,58,107,0.6), transparent 70%)",
          }}
        />

        {/* logo */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="TechNext"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              objectFit: "cover",
              bgcolor: "#fff",
              p: 0.25,
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: -0.3,
              }}
            >
              TechNext
            </Typography>
            <Typography
              sx={{ fontSize: 10, color: "#9db0cc", letterSpacing: 1.5 }}
            >
              RECRUITMENT PORTAL
            </Typography>
          </Box>
        </Box>

        {/* headline + features */}
        <Box sx={{ position: "relative" }}>
          <Typography
            sx={{
              fontSize: 34,
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: -0.8,
            }}
          >
            Hire smarter.
            <br />
            Track everything.
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "#aebbd0",
              mt: 2,
              mb: 3.5,
              lineHeight: 1.6,
              maxWidth: 380,
            }}
          >
            One workspace for positions, candidates, and the people moving them
            forward.
          </Typography>
          <Stack spacing={1.75}>
            {FEATURES.map((f) => (
              <Stack
                key={f.text}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Typography sx={{ fontSize: 13.5, color: "#dbe3ef" }}>
                  {f.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography
          sx={{ position: "relative", fontSize: 11.5, color: "#6b7a93" }}
        >
          © 2026 TechNext
        </Typography>
      </Box>

      {/* ---------- Form panel ---------- */}
      <Box
        sx={{
          flex: 1,
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* mobile logo */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.25,
              mb: 4,
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="TechNext"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                objectFit: "cover",
              }}
            />
            <Typography sx={{ fontSize: 17, fontWeight: 800 }}>
              TechNext
            </Typography>
          </Box>

          <Typography
            variant="h4"
            sx={{ fontWeight: 800, letterSpacing: -0.5, mb: 0.75 }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 4 }}>
            Sign in to your recruitment portal
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldErr.email) setFieldErr((p) => ({ ...p, email: "" }));
            }}
            onKeyDown={onKeyDown}
            error={!!fieldErr.email}
            helperText={fieldErr.email}
            sx={{ mb: 2 }}
            placeholder="you@company.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Password"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErr.password)
                setFieldErr((p) => ({ ...p, password: "" }));
            }}
            onKeyDown={onKeyDown}
            error={!!fieldErr.password}
            helperText={fieldErr.password}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw(!showPw)}
                    edge="end"
                    size="small"
                    aria-label="toggle password visibility"
                  >
                    {showPw ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
            sx={{ height: 48, fontSize: 15, fontWeight: 700 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Sign in"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
